import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { RootLogger } from "loglevel";
import { infraApi } from "../../app/api/infrastructureApi";
import store from "../../app/stores/store";
import { LogLevelName } from "../../enums/log/logLevelName";
import { LogLevelType } from "../../enums/log/logLevelType";
import { ClsLog } from "../../models/logging/clsLog";
import { Log } from "../../models/logging/log";
import { LogLevelRemote } from "../../models/logging/logLevelRemote";
import { isPartialTargetObject, isTargetObject } from "../objects/useGuard";
import { QueryReturnValue } from "../../models/baseQueries/baseQueryTypes";

export interface StacktraceOptions {
  levels: LogLevelType[];
  depth: number;
  excess: number;
}

export interface LogLevelRemoteOptions {
  level: LogLevelType;
  stacktrace: StacktraceOptions;
  timestamp: () => string;
  format: (log: LogLevelRemote, cls?: ClsLog, cprNumber?: string) => Log | string;
}

const jsonFormat = (log: LogLevelRemote, cls?: ClsLog, cprNumber?: string) =>
  ({
    msg: log.message,
    level: log.level.label,
    stacktrace: log.stacktrace,
    cprNumber: cprNumber ?? "",
    clsRequest: { ...cls },
  }) as Log;

const getStacktrace = () => {
  try {
    throw new Error();
  } catch (trace) {
    return (trace as Error)?.stack ?? "";
  }
};

const interpolate = (array: Array<string>): string => {
  let result = "";
  if (array.length > 0) {
    if (result) result += " ";
    result += array.join(" ");
  }
  return result;
};

const defaults = {
  level: LogLevelType.trace,
  stacktrace: {
    levels: [LogLevelType.trace, LogLevelType.warn, LogLevelType.error],
    depth: 3,
    excess: 0,
  },
  timestamp: () => new Date().toISOString(),
  format: jsonFormat,
} as LogLevelRemoteOptions;

const hasStacktraceSupport = !!getStacktrace();

const assign = <T>(...sources: T[]): T =>
  sources.reduce((target, source) => {
    for (const key in source) {
      if (Object.hasOwnProperty.call(source, key)) {
        target[key as keyof T] =
          typeof source[key] === "object" && !Array.isArray(source[key])
            ? assign(target[key as keyof T], source[key] as T[keyof T])
            : (source[key] as T[keyof T]);
      }
    }
    return target;
  }, {} as T);

const validateCLS = (logger: RootLogger, log: Log) => {
  let isValid = true;
  if (!log.clsRequest) {
    logger.error("LogLevelRemote error. ClsRequest object is absent.", "InnerError");
    isValid = false;
  }
  return isValid;
};

export const logLevelRemote = {
  apply: (logger: RootLogger, options: Partial<LogLevelRemoteOptions>) => {
    if (!logger || !logger.getLogger) {
      throw new TypeError("Argument is not a root loglevel object");
    }
    let isAsyncError = false;
    const config = assign(defaults, options) as LogLevelRemoteOptions;

    const originalMethod = logger.methodFactory;
    logger.methodFactory = (methodName, logLevelNumber, loggerName) => {
      const rawMethod = originalMethod(methodName, logLevelNumber, loggerName);
      const needStack =
        hasStacktraceSupport &&
        config.stacktrace.levels.some((level) => {
          return LogLevelType[level] === methodName;
        });
      const needLog = logLevelNumber >= config.level;

      return (...messages: Array<string | ClsLog | Log>) => {
        if (messages.length > 1 && messages[1] === "InnerError") {
          rawMethod(...messages);
          return;
        }
        if (needLog) {
          const timestamp = config.timestamp();

          let stacktrace = needStack ? getStacktrace() : "";
          if (stacktrace) {
            const lines = stacktrace.split("\n");
            lines.splice(0, config.stacktrace.excess + 3);
            const depth = config.stacktrace.depth;

            if (depth && lines.length !== depth + 1) {
              const shrink = lines.splice(0, depth);
              stacktrace = shrink.join("\n");
              if (lines.length) stacktrace += "\n    and " + lines.length + " more";
            } else {
              stacktrace = lines.join("\n");
            }
          }
          let clsLog: ClsLog | undefined = undefined;
          let cprNumber: string | undefined = undefined;
          const textMsgs: Array<string> = [];

          messages.forEach((data) => {
            if (typeof data === "string") textMsgs.push(data);
            else if (isPartialTargetObject<ClsLog>(data, ["logType", "requestId"])) {
              clsLog = data as ClsLog;
            } else if (isTargetObject<Log>(data, ["cprNumber"])) {
              cprNumber = (data as Log).cprNumber;
            } else {
              textMsgs.push(JSON.stringify(data));
            }
          });

          const mainFormatInput = {
            message: interpolate(textMsgs),
            level: {
              label: methodName,
              value: logLevelNumber,
            },
            logger: loggerName || "",
            timestamp: timestamp,
            stacktrace: stacktrace,
          };
          const log = config.format(mainFormatInput, clsLog, cprNumber);

          if (typeof log !== "string") {
            if (validateCLS(logger, log as Log) && !isAsyncError) {
              store.dispatch(infraApi.endpoints.addLogData.initiate(log as Log)).then((res) => {
                if ((res as QueryReturnValue<FetchBaseQueryError>).error) {
                  isAsyncError = true;
                }
              });
            }
          } else {
            rawMethod(log);
          }
          if (loggerName !== LogLevelName.NoConsoleInputLog) {
            rawMethod(...textMsgs);
          }
        }
      };
    };
    logger.setLevel(logger.getLevel());
    return logger;
  },
};
